import { $isLinkNode, $toggleLink, LinkNode } from '@lexical/link'
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListItemNode, ListNode, REMOVE_LIST_COMMAND } from '@lexical/list'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { $createHorizontalRuleNode, HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { $createHeadingNode, $createQuoteNode, $isHeadingNode, $isQuoteNode, HeadingNode, QuoteNode, type HeadingTagType } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { $findMatchingParent, $insertNodeToNearestRoot, mergeRegister } from '@lexical/utils'
import {
  $createParagraphNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  type EditorState,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
  type SerializedEditorState,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { Bold, ImagePlus, Italic, Link2, List, ListOrdered, Minus, Strikethrough, Underline, Unlink } from 'lucide-react'

import { miniaturaDe, tarjetaDe, useMedio } from '../../api/medios'
import { Boton, Modal, Selector, claseDeEntrada, clases } from '../../componentes/ui'
import type { Campo } from '../esquema'
import { useFormulario, useValor } from '../Formulario'
import { Envoltura } from './Controles'
import { SelectorDeMedios } from './Medio'
import { Buscador, TituloDeDocumento } from './Relacion'

/**
 * El editor de texto de los clientes, con **exactamente** las funciones que el núcleo
 * admite (apps/nucleo/src/contenido/editor.ts): párrafos, títulos h2–h4, negrita,
 * cursiva, subrayado, tachado, listas, cita, separador, enlaces (a una dirección o a
 * una página o entrada de la misma web) e imágenes de la biblioteca. Nada más: lo que
 * este editor no produce, el núcleo lo descarta al guardar.
 *
 * Lo guardado es el JSON de Lexical con la forma que ya tienen todos los documentos.
 * Dos nodos son de Payload y no de Lexical —el enlace, que guarda `fields`, y la
 * imagen (`upload`)— y por eso aquí tienen su clase; el enlace viaja como `enlace`
 * dentro del editor y se renombra a `link` al salir, porque `link` ya es el nodo de
 * Lexical y los dos no pueden convivir con el mismo nombre.
 */

// --- El enlace de Payload -------------------------------------------------------------------

type CamposDeEnlace = { linkType: 'custom' | 'internal'; url?: null | string; newTab?: boolean | null; doc?: null | { relationTo: string; value: number } }

type EnlaceSerializado = Spread<{ fields?: CamposDeEnlace }, SerializedLexicalNode> & { direction?: null | 'ltr' | 'rtl'; format?: string | number; indent?: number }

class EnlaceNode extends LinkNode {
  __campos: CamposDeEnlace

  static getType(): string {
    return 'enlace'
  }

  static clone(node: EnlaceNode): EnlaceNode {
    return new EnlaceNode(node.__campos, node.__key)
  }

  constructor(campos: CamposDeEnlace, key?: NodeKey) {
    super(campos.url || '#', { rel: campos.newTab ? 'noopener' : null, target: campos.newTab ? '_blank' : null }, key)
    this.__campos = campos
  }

  getCampos(): CamposDeEnlace {
    return this.getLatest().__campos
  }

  setCampos(campos: CamposDeEnlace): void {
    const escribible = this.getWritable()
    escribible.__campos = campos
    escribible.setURL(campos.url || '#')
    escribible.setTarget(campos.newTab ? '_blank' : null)
  }

  static importJSON(json: EnlaceSerializado): EnlaceNode {
    const nodo = $crearEnlace(json.fields ?? { linkType: 'custom', url: '' })
    if (typeof json.format === 'string') nodo.setFormat(json.format as never)
    if (typeof json.indent === 'number') nodo.setIndent(json.indent)
    if (json.direction !== undefined) nodo.setDirection(json.direction ?? null)
    return nodo
  }

  exportJSON(): ReturnType<LinkNode['exportJSON']> & { fields: CamposDeEnlace } {
    // Lo de LinkNode (`url`, `rel`, `target`, `title`) se quita al salir, en `aPayload`.
    return { ...super.exportJSON(), type: 'enlace', version: 3, fields: this.getCampos() }
  }
}

const $crearEnlace = (campos: CamposDeEnlace) => new EnlaceNode(campos)
const $esEnlace = (nodo: LexicalNode | null | undefined): nodo is EnlaceNode => nodo instanceof EnlaceNode

// --- La imagen (nodo `upload` de Payload) --------------------------------------------------

type MedioSerializado = Spread<{ relationTo: string; value: number | { id: number }; fields?: unknown; format?: string }, SerializedLexicalNode>

const ImagenEnTexto = ({ id }: { id: number }) => {
  const { data } = useMedio(id)
  const src = tarjetaDe(data) ?? miniaturaDe(data)
  return (
    <figure className="my-3 overflow-hidden rounded-lg border border-linea bg-papel-2">
      {src ? <img alt={data?.alt ?? ''} className="max-h-96 w-full object-contain" src={src} /> : <div className="p-6 text-center text-sm text-tenue">Imagen #{id}</div>}
      {data?.alt && <figcaption className="px-3 py-1 text-xs text-tenue">{data.alt}</figcaption>}
    </figure>
  )
}

class MedioNode extends DecoratorNode<ReactNode> {
  __id: number

  static getType(): string {
    return 'upload'
  }

  static clone(node: MedioNode): MedioNode {
    return new MedioNode(node.__id, node.__key)
  }

  constructor(id: number, key?: NodeKey) {
    super(key)
    this.__id = id
  }

  static importJSON(json: MedioSerializado): MedioNode {
    const id = typeof json.value === 'object' && json.value ? json.value.id : json.value
    return new MedioNode(Number(id))
  }

  exportJSON(): MedioSerializado {
    return { type: 'upload', version: 3, relationTo: 'medios', value: this.__id, fields: null, format: '' }
  }

  createDOM(): HTMLElement {
    const div = document.createElement('div')
    div.className = 'texto-rico__imagen'
    return div
  }

  updateDOM(): boolean {
    return false
  }

  isInline(): boolean {
    return false
  }

  decorate(): ReactNode {
    return <ImagenEnTexto id={this.__id} />
  }
}

// --- Entre el JSON de Payload y el del editor -------------------------------------------------

type Nodo = { type?: string; children?: Nodo[] } & Record<string, unknown>

const renombrar = (nodo: Nodo, de: string[], a: string): Nodo => ({
  ...nodo,
  ...(nodo.type && de.includes(nodo.type) ? { type: a } : {}),
  ...(Array.isArray(nodo.children) ? { children: nodo.children.map((h) => renombrar(h, de, a)) } : {}),
})

const alEditor = (valor: unknown): null | SerializedEditorState => {
  const raiz = (valor as { root?: Nodo } | null | undefined)?.root
  if (!raiz || !Array.isArray(raiz.children) || raiz.children.length === 0) return null
  return { root: renombrar(raiz, ['link', 'autolink'], 'enlace') } as unknown as SerializedEditorState
}

/** Al salir: `enlace` vuelve a ser `link`, y lo que era del LinkNode de Lexical no viaja. */
const limpiarEnlaces = (nodo: Nodo): Nodo => {
  const { url, rel, target, title, isUnlinked, ...resto } = nodo as Nodo & { isUnlinked?: unknown; rel?: unknown; target?: unknown; title?: unknown; url?: unknown }
  void url
  void rel
  void target
  void title
  void isUnlinked
  const limpio = nodo.type === 'enlace' ? { ...resto, type: 'link' } : nodo
  return Array.isArray(limpio.children) ? { ...limpio, children: limpio.children.map(limpiarEnlaces) } : limpio
}

const aPayload = (estado: SerializedEditorState): unknown => ({ root: limpiarEnlaces(estado.root as unknown as Nodo) })

// --- La barra de herramientas -----------------------------------------------------------------

type Bloque = 'h2' | 'h3' | 'h4' | 'number' | 'bullet' | 'paragraph' | 'quote'

const BotonDeBarra = ({ activo, etiqueta, onClick, children }: { activo?: boolean; children: ReactNode; etiqueta: string; onClick: () => void }) => (
  <button
    aria-label={etiqueta}
    aria-pressed={activo}
    className={clases('inline-flex size-8 items-center justify-center rounded-boton transition-colors [&>svg]:size-4', activo ? 'bg-violeta-suave text-violeta' : 'text-tinta-2 hover:bg-papel-3 hover:text-tinta')}
    onClick={onClick}
    onMouseDown={(e) => e.preventDefault()}
    title={etiqueta}
    type="button"
  >
    {children}
  </button>
)

const Barra = ({ soloLectura }: { soloLectura: boolean }) => {
  const [editor] = useLexicalComposerContext()
  const { sitio, espacio } = useFormulario()
  const [formatos, setFormatos] = useState({ bold: false, italic: false, underline: false, strikethrough: false })
  const [bloque, setBloque] = useState<Bloque>('paragraph')
  const [enlace, setEnlace] = useState<CamposDeEnlace | null>(null)
  const [dialogo, setDialogo] = useState<null | 'enlace' | 'imagen'>(null)

  const leer = useCallback(() => {
    const seleccion = $getSelection()
    if (!$isRangeSelection(seleccion)) return
    setFormatos({
      bold: seleccion.hasFormat('bold'),
      italic: seleccion.hasFormat('italic'),
      underline: seleccion.hasFormat('underline'),
      strikethrough: seleccion.hasFormat('strikethrough'),
    })
    const ancla = seleccion.anchor.getNode()
    const elemento = ancla.getKey() === 'root' ? ancla : $findMatchingParent(ancla, (n) => n.getParent()?.getKey() === 'root') ?? ancla.getTopLevelElementOrThrow()
    if ($isHeadingNode(elemento)) setBloque(elemento.getTag() as Bloque)
    else if ($isListNode(elemento)) setBloque(elemento.getListType() === 'number' ? 'number' : 'bullet')
    else if ($isQuoteNode(elemento)) setBloque('quote')
    else setBloque('paragraph')
    const nodoEnlace = $findMatchingParent(ancla, (n) => $esEnlace(n) || $isLinkNode(n))
    setEnlace($esEnlace(nodoEnlace) ? nodoEnlace.getCampos() : null)
  }, [])

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(({ editorState }) => editorState.read(leer)),
        editor.registerCommand(SELECTION_CHANGE_COMMAND, () => (leer(), false), COMMAND_PRIORITY_LOW),
      ),
    [editor, leer],
  )

  const ponerBloque = (tipo: Bloque) => {
    if (tipo === 'bullet') return editor.dispatchCommand(bloque === 'bullet' ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND, undefined)
    if (tipo === 'number') return editor.dispatchCommand(bloque === 'number' ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND, undefined)
    editor.update(() => {
      const seleccion = $getSelection()
      if (!$isRangeSelection(seleccion)) return
      if (tipo === 'paragraph' || bloque === tipo) $setBlocksType(seleccion, () => $createParagraphNode())
      else if (tipo === 'quote') $setBlocksType(seleccion, () => $createQuoteNode())
      else $setBlocksType(seleccion, () => $createHeadingNode(tipo as HeadingTagType))
    })
  }

  const aplicarEnlace = (campos: CamposDeEnlace | null) => {
    editor.update(() => {
      $toggleLink(campos ? campos.url || '#' : null)
      if (!campos) return
      const seleccion = $getSelection()
      if (!$isRangeSelection(seleccion)) return
      for (const nodo of seleccion.getNodes()) {
        const padre = $findMatchingParent(nodo, (n) => $esEnlace(n))
        if ($esEnlace(padre)) padre.setCampos(campos)
      }
    })
    setDialogo(null)
  }

  if (soloLectura) return null

  return (
    <div className="relative flex flex-wrap items-center gap-0.5 border-b border-linea bg-papel-2/60 px-2 py-1.5">
      <Selector
        aria-label="Tipo de bloque"
        className="mr-1 w-32"
        pequeno
        onChange={(e) => ponerBloque(e.target.value as Bloque)}
        onMouseDown={(e) => e.stopPropagation()}
        value={bloque}
      >
        <option value="paragraph">Párrafo</option>
        <option value="h2">Título 2</option>
        <option value="h3">Título 3</option>
        <option value="h4">Título 4</option>
        <option value="quote">Cita</option>
      </Selector>
      <BotonDeBarra activo={formatos.bold} etiqueta="Negrita" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}>
        <Bold />
      </BotonDeBarra>
      <BotonDeBarra activo={formatos.italic} etiqueta="Cursiva" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}>
        <Italic />
      </BotonDeBarra>
      <BotonDeBarra activo={formatos.underline} etiqueta="Subrayado" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}>
        <Underline />
      </BotonDeBarra>
      <BotonDeBarra activo={formatos.strikethrough} etiqueta="Tachado" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}>
        <Strikethrough />
      </BotonDeBarra>
      <span className="mx-1.5 h-5 w-px bg-linea-fuerte" />
      <BotonDeBarra activo={bloque === 'bullet'} etiqueta="Lista" onClick={() => ponerBloque('bullet')}>
        <List />
      </BotonDeBarra>
      <BotonDeBarra activo={bloque === 'number'} etiqueta="Lista numerada" onClick={() => ponerBloque('number')}>
        <ListOrdered />
      </BotonDeBarra>
      <BotonDeBarra etiqueta="Separador" onClick={() => editor.update(() => $insertNodeToNearestRoot($createHorizontalRuleNode()))}>
        <Minus />
      </BotonDeBarra>
      <span className="mx-1.5 h-5 w-px bg-linea-fuerte" />
      <BotonDeBarra activo={enlace !== null} etiqueta={enlace ? 'Editar enlace' : 'Enlace'} onClick={() => setDialogo((d) => (d === 'enlace' ? null : 'enlace'))}>
        <Link2 />
      </BotonDeBarra>
      {enlace && (
        <BotonDeBarra etiqueta="Quitar enlace" onClick={() => aplicarEnlace(null)}>
          <Unlink />
        </BotonDeBarra>
      )}
      <BotonDeBarra etiqueta="Imagen de la biblioteca" onClick={() => setDialogo('imagen')}>
        <ImagePlus />
      </BotonDeBarra>

      {dialogo === 'enlace' && <DialogoDeEnlace actual={enlace} aplicar={aplicarEnlace} cerrar={() => setDialogo(null)} />}
      {dialogo === 'imagen' && sitio && (
        <SelectorDeMedios
          alElegir={(m) => {
            editor.update(() => {
              $insertNodes([new MedioNode(m.id), $createParagraphNode()])
            })
            setDialogo(null)
          }}
          cerrar={() => setDialogo(null)}
          espacio={espacio}
          sitio={sitio}
        />
      )}
    </div>
  )
}

/**
 * El enlace, en un diálogo centrado (el recuadro que colgaba de la barra se cortaba dentro
 * del campo, y en el móvil «Aplicar» quedaba fuera de la vista). Lo elegido se enseña por
 * su nombre —«Contacto»—, no por su colección y su número.
 */
const DialogoDeEnlace = ({ actual, aplicar, cerrar }: { actual: CamposDeEnlace | null; aplicar: (c: CamposDeEnlace) => void; cerrar: () => void }) => {
  const [modo, setModo] = useState<'custom' | 'internal'>(actual?.linkType ?? 'custom')
  const [url, setUrl] = useState(actual?.url ?? '')
  const [nuevaPestana, setNuevaPestana] = useState(Boolean(actual?.newTab))
  const [doc, setDoc] = useState<CamposDeEnlace['doc']>(actual?.doc ?? null)
  const [coleccion, setColeccion] = useState<'paginas' | 'entradas'>((actual?.doc?.relationTo as 'paginas' | 'entradas') ?? 'paginas')
  const listo = modo === 'custom' ? Boolean(url.trim()) : Boolean(doc)
  const confirmar = () => {
    if (!listo) return
    aplicar(modo === 'custom' ? { linkType: 'custom', url: url.trim(), newTab: nuevaPestana, doc: null } : { linkType: 'internal', doc, newTab: nuevaPestana, url: undefined })
  }
  return (
    <Modal
      ancho="max-w-lg"
      cerrar={cerrar}
      pie={
        <>
          <Boton onClick={cerrar} tipo="discreto">
            Cancelar
          </Boton>
          <Boton disabled={!listo} onClick={confirmar} tipo="primario">
            Aplicar
          </Boton>
        </>
      }
      titulo={actual ? 'Editar el enlace' : 'Añadir un enlace'}
    >
      <div className="flex min-h-64 flex-col gap-3">
        <div className="flex gap-1 text-sm" role="tablist">
          <button aria-selected={modo === 'custom'} className={clases('rounded-boton px-2.5 py-1 text-[13px]', modo === 'custom' ? 'bg-violeta-suave font-medium text-violeta' : 'text-tinta-2 hover:bg-papel-2')} onClick={() => setModo('custom')} role="tab" type="button">
            A una dirección
          </button>
          <button aria-selected={modo === 'internal'} className={clases('rounded-boton px-2.5 py-1 text-[13px]', modo === 'internal' ? 'bg-violeta-suave font-medium text-violeta' : 'text-tinta-2 hover:bg-papel-2')} onClick={() => setModo('internal')} role="tab" type="button">
            A una página o entrada de tu web
          </button>
        </div>
        {modo === 'custom' ? (
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Dirección</span>
            <input
              autoFocus
              className={claseDeEntrada()}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  confirmar()
                }
              }}
              placeholder="https://…"
              type="url"
              value={url}
            />
          </label>
        ) : (
          <div className="flex flex-col gap-2">
            <Selector aria-label="Qué buscar" onChange={(e) => setColeccion(e.target.value as 'paginas' | 'entradas')} pequeno value={coleccion}>
              <option value="paginas">Páginas</option>
              <option value="entradas">Entradas del blog</option>
            </Selector>
            <Buscador alElegir={(d) => setDoc({ relationTo: coleccion, value: d.id })} autoFoco coleccion={coleccion} excluir={[]} />
            {doc && (
              <p className="text-[13px] text-tinta-2">
                Enlaza a{' '}
                <strong className="font-medium text-tinta">
                  «<TituloDeDocumento coleccion={doc.relationTo} id={doc.value} />»
                </strong>
                {doc.relationTo === 'entradas' ? ' (entrada del blog)' : ''}.
              </p>
            )}
          </div>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input checked={nuevaPestana} className="accent-violeta" onChange={(e) => setNuevaPestana(e.target.checked)} type="checkbox" />
          Abrir en una pestaña nueva
        </label>
      </div>
    </Modal>
  )
}

// --- El campo ----------------------------------------------------------------------------------

const TEMA = {
  paragraph: 'texto-rico__p',
  quote: 'texto-rico__cita',
  heading: { h2: 'texto-rico__h2', h3: 'texto-rico__h3', h4: 'texto-rico__h4' },
  list: { ul: 'texto-rico__ul', ol: 'texto-rico__ol', listitem: 'texto-rico__li', nested: { listitem: 'texto-rico__li-anidado' } },
  link: 'texto-rico__enlace',
  text: { bold: 'font-semibold', italic: 'italic', underline: 'underline', strikethrough: 'line-through', underlineStrikethrough: 'underline line-through' },
}

export const TextoRico = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<unknown>(ruta)
  const inicial = useMemo(() => alEditor(valor), []) // eslint-disable-line react-hooks/exhaustive-deps -- solo al abrir: después manda el editor

  const configuracion = useMemo(
    () => ({
      namespace: `texto-rico-${ruta}`,
      editable: !soloLectura,
      theme: TEMA,
      nodes: [
        HeadingNode,
        QuoteNode,
        ListNode,
        ListItemNode,
        HorizontalRuleNode,
        MedioNode,
        EnlaceNode,
        LinkNode,
        // `$toggleLink` crea un LinkNode de Lexical: se sustituye al vuelo por el nuestro.
        { replace: LinkNode, with: (nodo: LinkNode) => $crearEnlace({ linkType: 'custom', url: nodo.getURL(), newTab: nodo.getTarget() === '_blank' }), withKlass: EnlaceNode },
      ],
      editorState: inicial ? JSON.stringify(inicial) : undefined,
      onError: (e: Error) => {
        console.error(e)
      },
    }),
    [ruta, soloLectura, inicial],
  )

  const alCambiar = useCallback(
    (estado: EditorState, _editor: LexicalEditor, etiquetas: Set<string>) => {
      if (etiquetas.has('history-merge') && etiquetas.size === 1) return
      const json = estado.toJSON()
      const vacio = !json.root.children.length || (json.root.children.length === 1 && !((json.root.children[0] as { children?: unknown[] }).children ?? []).length && json.root.children[0]?.type === 'paragraph')
      cambiar(vacio ? null : aPayload(json))
    },
    [cambiar],
  )

  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className={clases('texto-rico overflow-hidden rounded-campo border bg-papel transition-colors', error ? 'border-error' : 'border-linea hover:border-linea-fuerte focus-within:border-violeta focus-within:shadow-foco')}>
        <LexicalComposer initialConfig={configuracion}>
          <Barra soloLectura={soloLectura} />
          <div className="relative">
            <RichTextPlugin
              contentEditable={<ContentEditable aria-label={campo.etiqueta} className="min-h-48 px-4 py-3 outline-none" id={ruta} />}
              ErrorBoundary={LexicalErrorBoundary}
              placeholder={<p className="pointer-events-none absolute top-3 left-4 text-tenue">Escribe aquí…</p>}
            />
          </div>
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <OnChangePlugin ignoreSelectionChange onChange={alCambiar} />
        </LexicalComposer>
      </div>
    </Envoltura>
  )
}
